import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Divider,
    CircularProgress,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import { API_ENDPOINTS, getData } from "../../services/apiService.js";

/**
 * Read-only chat audit panel for SYT-Admin.
 *
 * Mounts inside any per-shipment screen (e.g. PackageInfo) and renders
 * both chat channels (customer ↔ owner, customer ↔ driver) for the
 * given mapping_id. No compose UI — admins are observers only.
 *
 * Hits B4 admin endpoints which honour the x-user-type=admin header
 * that apiService.js auto-injects.
 *
 * Props:
 *   mappingId — required, the syt_truck_package_mapping.mapping_id
 *               whose threads should be audited.
 */
function ChatAuditPanel({ mappingId }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [threads, setThreads] = useState([]);
    const [messagesByThread, setMessagesByThread] = useState({});

    const formatTs = (ts) => {
        if (!ts) return "";
        try {
            const d = new Date(ts);
            return d.toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return "";
        }
    };

    const channelLabel = (t) =>
        t.channel_type === "customer_owner"
            ? "Customer ↔ Owner"
            : "Customer ↔ Driver";

    const senderLabel = (m) => {
        if (m.is_system_message || m.sender_user_type === "system") return "System";
        const map = {
            customer: "Customer",
            owner: "Owner",
            driver: "Driver",
        };
        return map[m.sender_user_type] || m.sender_user_type;
    };

    const fetchAll = useCallback(async () => {
        if (!mappingId) return;
        setLoading(true);
        setError("");
        try {
            // 1. Threads for this mapping
            const threadsRes = await getData(API_ENDPOINTS.chatAdminThreads, {
                mapping_id: mappingId,
            });
            const tList = threadsRes?.result?.threads || [];
            setThreads(tList);

            // 2. Messages per thread (cap at default 100; admin can scroll)
            const msgMap = {};
            await Promise.all(
                tList.map(async (t) => {
                    try {
                        const url = API_ENDPOINTS.chatAdminThreadMessages.replace(
                            ":thread_id",
                            String(t.thread_id)
                        );
                        const res = await getData(url, { limit: 200 });
                        msgMap[t.thread_id] = res?.result?.messages || [];
                    } catch (e) {
                        msgMap[t.thread_id] = [];
                    }
                })
            );
            setMessagesByThread(msgMap);
        } catch (e) {
            setError(e?.response?.data?.message || e.message || "Failed to load chat history");
        } finally {
            setLoading(false);
        }
    }, [mappingId]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    if (!mappingId) return null;

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Box display="flex" alignItems="center" gap={1.25} mb={2}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            background: "linear-gradient(135deg,#1A73E8,#4285F4)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 3px 8px rgba(26,115,232,0.25)",
                        }}
                    >
                        <ChatRoundedIcon sx={{ color: "white", fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight="bold">
                        Chat history (read-only)
                    </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {loading && (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {!loading && error && (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                )}

                {!loading && !error && threads.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No chat threads exist for this shipment.
                    </Typography>
                )}

                {!loading && !error && threads.map((t) => {
                    const messages = messagesByThread[t.thread_id] || [];
                    return (
                        <Accordion key={t.thread_id} defaultExpanded sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" alignItems="center" gap={1} flex={1}>
                                    <Typography fontWeight={600}>{channelLabel(t)}</Typography>
                                    <Chip
                                        label={t.status === "closed" ? "Closed" : "Open"}
                                        size="small"
                                        color={t.status === "closed" ? "default" : "success"}
                                        variant="outlined"
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        {messages.length} message{messages.length === 1 ? "" : "s"}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {messages.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No messages in this thread.
                                    </Typography>
                                ) : (
                                    <Box display="flex" flexDirection="column" gap={1.25}>
                                        {messages.map((m) => (
                                            <Box
                                                key={m.message_id}
                                                sx={{
                                                    p: 1.25,
                                                    borderRadius: 1.5,
                                                    bgcolor:
                                                        m.is_system_message || m.sender_user_type === "system"
                                                            ? "#f1f3f4"
                                                            : "#fff",
                                                    border:
                                                        m.is_system_message || m.sender_user_type === "system"
                                                            ? "none"
                                                            : "1px solid #e0e0e0",
                                                    fontStyle:
                                                        m.is_system_message || m.sender_user_type === "system"
                                                            ? "italic"
                                                            : "normal",
                                                }}
                                            >
                                                <Box display="flex" justifyContent="space-between" mb={0.25}>
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                        color={
                                                            m.is_system_message || m.sender_user_type === "system"
                                                                ? "text.secondary"
                                                                : "primary"
                                                        }
                                                    >
                                                        {senderLabel(m)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatTs(m.createdAt)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2">{m.body}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </CardContent>
        </Card>
    );
}

export default ChatAuditPanel;
